import type { MaybeRef, Reactive } from "vue"
import type { MetaV1, ServerResponseV1 } from "@/http"
import { useQuery } from "@tanstack/vue-query"
import { ref, toValue } from "vue"
import z from "zod"
import { v1 } from "@/axios"

export interface IListRsvpAttendeesFilters {
  page?: number
  perPage?: number
}

const attendingGuestSchema = z.object({
  name: z.string(),
  additionalGuestCount: z.number(),
})

const schema = z.object({
  totalAttending: z.number().optional(),
  rsvpGuestList: z.array(attendingGuestSchema).optional(),
})

export async function listRsvpAttendees(eventVaultId: number, filters: Reactive<IListRsvpAttendeesFilters> = {}) {
  const endpoint = `/events/${eventVaultId}/rsvp/attendees`
  const params = new URLSearchParams()

  if (filters.page)
    params.append("page", filters.page.toString())
  if (filters.perPage)
    params.append("perPage", filters.perPage.toString())

  const response = await v1.get<ServerResponseV1<z.infer<typeof schema>>>(endpoint, { params })

  const parsedResponse = schema.safeParse(response.data.data)

  if (!parsedResponse.success) {
    throw new Error("Failed to safely parse the API response", { cause: parsedResponse.error })
  }

  return {
    ...parsedResponse.data,
    meta: response.data.meta as MetaV1 | undefined,
  }
}

const LIST_RSVP_ATTENDEES_KEY = "listRsvpAttendees"

export function useListRsvpAttendees(eventVaultId?: MaybeRef<number | undefined>, filters: Reactive<IListRsvpAttendeesFilters> = {}) {
  const pageCount = ref(0)

  const query = useQuery({
    queryKey: [LIST_RSVP_ATTENDEES_KEY, eventVaultId, filters],
    queryFn: async () => {
      const response = await listRsvpAttendees(toValue(eventVaultId)!, filters)

      if (response.meta?.totalPages) {
        pageCount.value = response.meta.totalPages
      }

      return response
    },
    enabled: !!toValue(eventVaultId),
  })

  return {
    ...query,
    pageCount,
  }
}
